/**
 * useCanonicalNetworkLayer - Hook para visualizar red canónica en el mapa
 *
 * @version 5.0
 * @created 2026-03-01
 * @updated 2026-03-02 - Estilos configurables via NetworkStyleContext
 * @module core/spatial/hooks
 *
 * @description
 * Gestiona la visualización de la red vial canónica en MapLibre:
 * - Edges: Líneas que conectan nodos (segmentos de calle)
 * - Nodes: Puntos de intersección con relleno sólido
 *
 * Geometría:
 * - Edges van de centro a centro de nodos
 * - Sin acortamiento ni modificación de geometrías
 * - Visualización directa de datos desde la base de datos
 *
 * Estética:
 * - Los estilos son configurables por el usuario via NetworkStylePanel
 * - Estilos se obtienen del NetworkStyleContext
 * - Cambios en estilos se aplican dinámicamente al mapa
 *
 * @example
 * ```typescript
 * useCanonicalNetworkLayer({
 *   projectId: 'abc-123',
 *   visible: true,
 *   hasData: networkStats.edgeCount > 0
 * });
 * ```
 */

import { useEffect, useRef } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { mapEngine } from '../../map-engine/MapEngineService';
import { useNetworkStyle } from '../contexts/NetworkStyleContext';
import { useUrbanNetworkSafe } from '../UrbanNetworkContext';
import { useNetworkEditSafe } from '../contexts/NetworkEditContext';
import { canonicalNetworkStore, rawArrayToFeatures } from '../stores/canonical-network-store';
import { getActiveOpeningPerfSessionId, markOpeningPerfEvent } from '../../utils';

export interface UseCanonicalNetworkLayerConfig {
  /** ID del proyecto */
  projectId: string | null;
  /** Visibilidad de la capa */
  visible: boolean;
  /** Si hay datos de red canónica */
  hasData: boolean;
}

const SOURCE_ID = 'canonical-network';
const PREVIEW_SOURCE_ID = 'canonical-network-preview'; // Source separado para previews
const FREEHAND_PREVIEW_SOURCE_ID = 'freehand-segment-preview'; // Source para preview de segmento a mano alzada
const EDGES_LAYER_ID = 'canonical-network-edges';
const NODES_LAYER_ID = 'canonical-network-nodes';
const EDGES_PREVIEW_LAYER_ID = 'canonical-network-edges-preview';
const NODES_PREVIEW_LAYER_ID = 'canonical-network-nodes-preview';
const FREEHAND_PREVIEW_LINE_LAYER_ID = 'freehand-segment-preview-line';
const FREEHAND_PREVIEW_POINTS_LAYER_ID = 'freehand-segment-preview-points';
const NETWORK_REVEAL_DURATION_MS = 360;

// ── SPIKE: Preview de carga (backbone vial mientras carga red completa) ──────
const SPIKE_PREVIEW_SOURCE    = 'nw-loading-preview';
const SPIKE_PREVIEW_GLOW_LAYER = 'nw-loading-preview-glow';
const SPIKE_PREVIEW_LINE_LAYER = 'nw-loading-preview-line';
const SPIKE_PREVIEW_NODE_LAYER = 'nw-loading-preview-nodes';
const SPIKE_ROAD_PRIORITY: Record<string, number> = { trunk: 3, primary: 2, secondary: 1 };
const SPIKE_TRAVELERS_SOURCE        = 'nw-loading-travelers';
const SPIKE_TRAVELERS_LAYER         = 'nw-loading-travelers-layer';    // node blinkers (circle)
const SPIKE_SEGMENT_BLINKERS_LAYER  = 'nw-loading-seg-blinkers-layer'; // segment blinkers (line)
const SPIKE_NODE_BLINKER_COUNT      = 12;
const SPIKE_SEG_BLINKER_COUNT       = 18;
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Guard defensivo: verifica que el mapa tenga su estilo cargado.
 * MapLibre lanza TypeError si se llama getLayer/getSource antes de que
 * map.style exista internamente (style aun no cargado).
 */
function isMapStyleReady(map: MapLibreMap | null | undefined): map is MapLibreMap {
  if (!map) return false;
  try {
    return !!map.getStyle();
  } catch {
    return false;
  }
}

/** Cede el hilo principal entre operaciones pesadas para no bloquear el render */
function yieldToMainThread(): Promise<void> {
  return new Promise((resolve) => {
    const sched = (window as any).scheduler;
    if (sched && typeof sched.yield === 'function') {
      void (sched.yield() as Promise<void>).then(resolve);
    } else {
      setTimeout(resolve, 0);
    }
  });
}

function createNodeOpacityExpression(baseOpacity: number, revealOpacity: number) {
  return [
    'case',
    ['boolean', ['feature-state', 'hidden'], false],
    0,
    baseOpacity * revealOpacity,
  ];
}

function createEdgeOpacityExpression(baseOpacity: number, revealOpacity: number) {
  return [
    'case',
    ['boolean', ['feature-state', 'hidden'], false],
    0,
    ['boolean', ['feature-state', 'associated'], false],
    1.0 * revealOpacity,
    ['boolean', ['feature-state', 'selected'], false],
    1.0 * revealOpacity,
    ['boolean', ['feature-state', 'hover'], false],
    0.9 * revealOpacity,
    baseOpacity * revealOpacity,
  ];
}

// ── Network Stats Cache (optimistic hasData) ─────────────────────────────────
// Persiste edgeCount/nodeCount en localStorage para que el segundo arranque no
// espere el stats API (~14s) antes de disparar la carga completa de red.
const NETWORK_STATS_CACHE_VERSION = 1;
const NETWORK_STATS_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

interface NetworkStatsCache {
  version: number;
  projectId: string;
  edgeCount: number;
  nodeCount: number;
  cachedAt: number; // Date.now()
}

function _networkStatsCacheKey(projectId: string): string {
  return `wayline-network-stats-${projectId}`;
}

function _readNetworkStatsCache(projectId: string): NetworkStatsCache | null {
  try {
    const raw = localStorage.getItem(_networkStatsCacheKey(projectId));
    if (!raw) return null;
    return JSON.parse(raw) as NetworkStatsCache;
  } catch {
    return null;
  }
}

function _writeNetworkStatsCache(projectId: string, edgeCount: number, nodeCount: number): void {
  try {
    const entry: NetworkStatsCache = {
      version: NETWORK_STATS_CACHE_VERSION,
      projectId,
      edgeCount,
      nodeCount,
      cachedAt: Date.now(),
    };
    localStorage.setItem(_networkStatsCacheKey(projectId), JSON.stringify(entry));
    console.log(`[NetworkCache] escrito — edgeCount:${edgeCount} nodeCount:${nodeCount}`);
  } catch { /* ignore */ }
}

function _invalidateNetworkStatsCache(projectId: string, reason: string): void {
  try {
    localStorage.removeItem(_networkStatsCacheKey(projectId));
    console.log(`[NetworkCache] invalidado — ${reason}`);
  } catch { /* ignore */ }
}
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook para gestionar la visualización de red canónica
 */
export function useCanonicalNetworkLayer({
  projectId,
  visible,
  hasData,
}: UseCanonicalNetworkLayerConfig): void {
  const { style } = useNetworkStyle();
  const urbanNetwork = useUrbanNetworkSafe();
  const networkEdit = useNetworkEditSafe();
  // FIX: Usar selectedEdgeId de NetworkEditContext para consistencia con selectedNodeId
  const selectedEdgeId = networkEdit?.selectedEdgeId ?? urbanNetwork?.selectedEdgeId ?? null;
  // const hoveredEdgeId = urbanNetwork?.hoveredEdgeId ?? null; // Not used directly here
  const hoveredNodeId = urbanNetwork?.hoveredNodeId ?? null; // REFACTOR: Agregado para hover de nodos
  // const selectedNodeId = networkEdit?.selectedNodeId ?? null; // Not used directly here
  const editMode = networkEdit?.editMode ?? null;

  // Freehand Segment (Etapa 1)
  const freehandSourceCoords = networkEdit?.freehandSourceCoords ?? null;
  const freehandIntermediatePoints = networkEdit?.freehandIntermediatePoints ?? [];
  const freehandPreviewEdge = networkEdit?.freehandPreviewEdge ?? null;
  const isNetworkInteractive = urbanNetwork?.isNetworkInteractive ?? false; // UX: Nodos solo visibles cuando interactivo (desde MapControls)

  // FASE 1A: Obtener setter para loading state
  const setLoadingNetwork = urbanNetwork?.setLoadingNetwork;
  const setPreviewNetworkBooting = urbanNetwork?.setPreviewNetworkBooting; // SPIKE: map ready + fetch fired
  const setPreviewNetworkActive = urbanNetwork?.setPreviewNetworkActive; // SPIKE: backbone data rendered

  // ── SPIKE-PERF: Timing instrumentation ─────────────────────────────────
  const spikePerfT0Ref = useRef<number>(0);
  const spikePerfLog = (msg: string): void => {
    const delta = Math.round(performance.now() - spikePerfT0Ref.current);
    console.log(`[SPIKE-PERF] +${delta}ms ${msg}`);
  };
  // ────────────────────────────────────────────────────────────────────────

  // Fase 3: Merge edges selection state (V1 2-edge merge)
  const selectedEdgesForMerge = networkEdit?.selectedEdgesForMerge ?? [];
  const mergeValidationResult = networkEdit?.mergeValidationResult ?? null;

  // Multi-Merge V1 (Fase 3): Selection state
  const selectedEdgeIds = networkEdit?.selectedEdgeIds ?? [];
  const chainValidation = networkEdit?.chainValidation ?? null;

  const layersLoadedRef = useRef(false);
  const visibleRef = useRef(visible);
  const styleListenerRegisteredRef = useRef(false);
  const selectedEdgeIdRef = useRef<string | null>(null);
  // const selectedNodeIdRef = useRef<string | null>(null); // Not used currently
  // const hoveredEdgeIdRef = useRef<string | null>(null); // Not used currently
  const hoveredNodeIdRef = useRef<string | null>(null); // REFACTOR: Agregado para hover de nodos
  const editModeRef = useRef(editMode);

  // Fase 3: Ref para tracking de edges seleccionados para merge (V1 2-edge merge)
  const selectedEdgesForMergeRef = useRef<string[]>([]);

  // Multi-Merge V1 (Fase 3): Ref para tracking de edges seleccionados
  const selectedEdgeIdsRef = useRef<string[]>([]);

  // FASE 1A: Ref para protección contra cargas concurrentes
  const loadingRef = useRef(false);
  const revealOpacityRef = useRef(1);
  const revealFrameRef = useRef<number | null>(null);
  const revealEndTimeoutRef = useRef<number | null>(null);

  // SPIKE: Preview loading animation refs
  const spikeBreathRafRef       = useRef<number | null>(null);
  const spikeFadeOutRafRef      = useRef<number | null>(null);
  const spikeBackboneFiredRef   = useRef(false); // guard — fire backbone fetch only once per project
  const spikeNodePositionsRef   = useRef<number[][]>([]); // backbone intersection positions for blinker animation
  const spikeCurrentOpacityRef  = useRef(0);               // last rendered opacity (for fade-out start)
  const spikeEdgeCoordsRef      = useRef<number[][][]>([]); // edge coordinate arrays for segment blinkers

  const applyRevealOpacity = (map: MapLibreMap, revealOpacity: number): void => {
    revealOpacityRef.current = revealOpacity;

    if (map.getLayer(EDGES_LAYER_ID)) {
      map.setPaintProperty(
        EDGES_LAYER_ID,
        'line-opacity',
        createEdgeOpacityExpression(style.edges.opacity, revealOpacity)
      );
    }

    if (map.getLayer(NODES_LAYER_ID)) {
      const nodeOpacityExpression = createNodeOpacityExpression(style.nodes.opacity, revealOpacity);
      map.setPaintProperty(NODES_LAYER_ID, 'circle-opacity', nodeOpacityExpression);
      map.setPaintProperty(NODES_LAYER_ID, 'circle-stroke-opacity', nodeOpacityExpression);
    }
  };

  const configureRevealTransitions = (map: MapLibreMap): void => {
    if (map.getLayer(EDGES_LAYER_ID)) {
      map.setPaintProperty(EDGES_LAYER_ID, 'line-opacity-transition', {
        duration: NETWORK_REVEAL_DURATION_MS,
        delay: 0,
      });
    }

    if (map.getLayer(NODES_LAYER_ID)) {
      const transition = {
        duration: NETWORK_REVEAL_DURATION_MS,
        delay: 0,
      };

      map.setPaintProperty(NODES_LAYER_ID, 'circle-opacity-transition', transition);
      map.setPaintProperty(NODES_LAYER_ID, 'circle-stroke-opacity-transition', transition);
    }
  };

  const hideNetworkForReveal = (map: MapLibreMap): void => {
    if (revealFrameRef.current !== null) {
      cancelAnimationFrame(revealFrameRef.current);
      revealFrameRef.current = null;
    }

    if (revealEndTimeoutRef.current !== null) {
      window.clearTimeout(revealEndTimeoutRef.current);
      revealEndTimeoutRef.current = null;
    }

    applyRevealOpacity(map, 0);
  };

  const revealNetwork = (map: MapLibreMap, sessionId: string | null): void => {
    configureRevealTransitions(map);

    if (revealFrameRef.current !== null) {
      cancelAnimationFrame(revealFrameRef.current);
    }

    if (revealEndTimeoutRef.current !== null) {
      window.clearTimeout(revealEndTimeoutRef.current);
      revealEndTimeoutRef.current = null;
    }

    revealFrameRef.current = window.requestAnimationFrame(() => {
      markOpeningPerfEvent(sessionId, 'network:fade-start');
      applyRevealOpacity(map, 1);
      revealEndTimeoutRef.current = window.setTimeout(() => {
        markOpeningPerfEvent(sessionId, 'network:fade-end');
        revealEndTimeoutRef.current = null;
      }, NETWORK_REVEAL_DURATION_MS);
      revealFrameRef.current = null;
    });
  };

  // ── SPIKE: Preview helpers (hook scope — called from early backbone effect AND loadNetworkData) ──

  /**
   * SPIKE: Anima la red con nodos Y segmentos que se encienden/apagan individualmente.
   * Fondo estático tenue (blanco 28%) + blinkers sobre intersecciones reales y segmentos aleatorios.
   */
  const spikeStartBreath = (map: MapLibreMap): void => {
    const BG_OPACITY   = 0.28;
    const nodePositions = spikeNodePositionsRef.current;
    const edgeCoords    = spikeEdgeCoordsRef.current;

    type Blinker = { idx: number; age: number; maxAge: number };

    const nodeBlinkers: Blinker[] = Array.from({ length: SPIKE_NODE_BLINKER_COUNT }, () => ({
      idx:    Math.floor(Math.random() * Math.max(1, nodePositions.length)),
      age:    Math.random() * 2000,
      maxAge: 1200 + Math.random() * 1800,
    }));

    const segBlinkers: Blinker[] = Array.from({ length: SPIKE_SEG_BLINKER_COUNT }, () => ({
      idx:    Math.floor(Math.random() * Math.max(1, edgeCoords.length)),
      age:    Math.random() * 2600,
      maxAge: 1400 + Math.random() * 2200,
    }));

    let lastTs = performance.now();

    const animFrame = (ts: number): void => {
      const dt = Math.min(ts - lastTs, 50);
      lastTs = ts;

      spikeCurrentOpacityRef.current = BG_OPACITY;

      // Fondo estático tenue — sin breath, sin distracción
      if (map.getLayer(SPIKE_PREVIEW_LINE_LAYER)) {
        map.setPaintProperty(SPIKE_PREVIEW_GLOW_LAYER, 'line-opacity', BG_OPACITY * 0.5);
        map.setPaintProperty(SPIKE_PREVIEW_LINE_LAYER, 'line-opacity', BG_OPACITY);
      }

      // Node blinkers: se encienden y apagan en intersecciones reales
      const nodeFeatures = nodePositions.length > 0
        ? nodeBlinkers.map(b => {
            b.age += dt;
            if (b.age >= b.maxAge) {
              b.age    = 0;
              b.idx    = Math.floor(Math.random() * nodePositions.length);
              b.maxAge = 1200 + Math.random() * 1800;
            }
            const bOpacity = Math.sin((b.age / b.maxAge) * Math.PI);
            return {
              type: 'Feature' as const,
              geometry: { type: 'Point' as const, coordinates: nodePositions[b.idx] },
              properties: { bOpacity },
            };
          })
        : [];

      // Segment blinkers: segmentos individuales que se iluminan y apagan
      const segFeatures = edgeCoords.length > 0
        ? segBlinkers.map(b => {
            b.age += dt;
            if (b.age >= b.maxAge) {
              b.age    = 0;
              b.idx    = Math.floor(Math.random() * edgeCoords.length);
              b.maxAge = 1400 + Math.random() * 2200;
            }
            const bOpacity = Math.sin((b.age / b.maxAge) * Math.PI);
            return {
              type: 'Feature' as const,
              geometry: { type: 'LineString' as const, coordinates: edgeCoords[b.idx] },
              properties: { bOpacity },
            };
          })
        : [];

      const travelSource = map.getSource(SPIKE_TRAVELERS_SOURCE) as { setData: (d: unknown) => void } | undefined;
      travelSource?.setData({ type: 'FeatureCollection', features: [...nodeFeatures, ...segFeatures] });

      spikeBreathRafRef.current = requestAnimationFrame(animFrame);
    };

    spikeBreathRafRef.current = requestAnimationFrame(animFrame);
  };

  const spikeFadeOut = (map: MapLibreMap): void => {
    if (spikeBreathRafRef.current !== null) {
      cancelAnimationFrame(spikeBreathRafRef.current);
      spikeBreathRafRef.current = null;
    }
    const DURATION = 700;
    const startOpacity = spikeCurrentOpacityRef.current || 0.28;
    const startTs = performance.now();
    const fadeFrame = (ts: number): void => {
      const progress = Math.min(1, (ts - startTs) / DURATION);
      const opacity = startOpacity * (1 - progress);
      if (map.getLayer(SPIKE_PREVIEW_LINE_LAYER)) {
        map.setPaintProperty(SPIKE_PREVIEW_GLOW_LAYER, 'line-opacity', opacity * 0.5);
        map.setPaintProperty(SPIKE_PREVIEW_LINE_LAYER, 'line-opacity', opacity);
      }
      if (map.getLayer(SPIKE_TRAVELERS_LAYER)) {
        map.setPaintProperty(SPIKE_TRAVELERS_LAYER, 'circle-opacity', opacity);
        map.setPaintProperty(SPIKE_TRAVELERS_LAYER, 'circle-stroke-opacity', opacity * 0.7);
      }
      if (map.getLayer(SPIKE_SEGMENT_BLINKERS_LAYER)) {
        map.setPaintProperty(SPIKE_SEGMENT_BLINKERS_LAYER, 'line-opacity', opacity);
      }
      if (progress < 1) {
        spikeFadeOutRafRef.current = requestAnimationFrame(fadeFrame);
      } else {
        spikeFadeOutRafRef.current = null;
        setPreviewNetworkActive?.(false);
      }
    };
    spikeFadeOutRafRef.current = requestAnimationFrame(fadeFrame);
  };

  /**
   * SPIKE: Carga líneas del backbone, extrae intersecciones reales (degree ≥ 2) y
   * guarda coordenadas de edges para el sistema de blinkers de segmentos.
   */
  const spikePopulatePreview = (map: MapLibreMap, edges: Record<string, unknown>[]): void => {
    const lineFeatures: unknown[] = [];
    const edgeCoords: number[][][] = [];
    const endpointCount = new Map<string, number>();
    const endpointPos   = new Map<string, number[]>();

    for (const edge of edges) {
      if (edge.geometry) {
        lineFeatures.push({ type: 'Feature', geometry: edge.geometry, properties: { id: edge.id } });
        const coords = (edge.geometry as { coordinates?: number[][] } | undefined)?.coordinates;
        if (Array.isArray(coords) && coords.length >= 2) {
          edgeCoords.push(coords as number[][]);
          // Contar cuántas aristas comparten cada endpoint para detectar intersecciones reales
          for (const pt of [coords[0], coords[coords.length - 1]] as number[][]) {
            const key = `${pt[0].toFixed(5)},${pt[1].toFixed(5)}`;
            endpointCount.set(key, (endpointCount.get(key) ?? 0) + 1);
            endpointPos.set(key, pt);
          }
        }
      }
    }

    // Intersecciones reales: endpoints compartidos por ≥ 2 aristas (degree ≥ 2)
    const nodePositions: number[][] = [];
    endpointCount.forEach((count, key) => {
      if (count >= 2) nodePositions.push(endpointPos.get(key)!);
    });

    // Fallback: si no hay intersecciones claras (red pequeña), usar todos los endpoints
    spikeNodePositionsRef.current = nodePositions.length > 0 ? nodePositions : [...endpointPos.values()];
    spikeEdgeCoordsRef.current = edgeCoords;

    const source = map.getSource(SPIKE_PREVIEW_SOURCE) as { setData: (d: unknown) => void } | undefined;
    source?.setData({ type: 'FeatureCollection', features: lineFeatures });

    console.log(`[SPIKE] Preview: ${lineFeatures.length} segs, ${nodePositions.length} intersections, ${edgeCoords.length} seg-blinker candidates`);
  };

  // ── End SPIKE helpers ────────────────────────────────────────────────────

  // Mantener editModeRef actualizado
  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  // Mantener ref actualizado
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  // ── SPIKE: Early backbone fetch ──────────────────────────────────────────
  // Two-track approach:
  //   Track A: Backbone fetch fires immediately at t=0ms (no map gate).
  //   Track B: Poll map.isStyleLoaded() (fires ~1s) to create SPIKE layers.
  //   Render when BOTH tracks complete — whichever arrives last triggers the draw.
  useEffect(() => {
    if (!projectId) {
      spikeBackboneFiredRef.current = false;
      return;
    }

    spikePerfT0Ref.current = performance.now();
    spikePerfLog(`projectId available: ${projectId}`);

    // Coordination state between the two parallel tracks
    let spikeFetchResult: Record<string, unknown>[] | null = null;
    let spikeFetchDone = false;
    let spikeLayersReady = false;
    let spikePollInterval: NodeJS.Timeout | null = null;
    let cancelled = false;

    // ── Track A: Fire backbone fetch immediately (no map gate) ──────────────
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';
    const authStorage = localStorage.getItem('auth-storage');
    let token: string | null = null;
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
        token = parsed.state?.token || null;
      } catch { /* ignore */ }
    }
    const fetchHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) fetchHeaders['Authorization'] = `Bearer ${token}`;

    spikePerfLog('backbone preview fetch START (immediate, no map gate) →');
    fetch(
      `${API_URL}/projects/${projectId}/network/preview?roadClasses=trunk,primary,secondary&limit=5000`,
      { headers: fetchHeaders }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((edges: Record<string, unknown>[] | null) => {
        if (cancelled) return;
        spikeFetchDone = true;
        spikeFetchResult = edges ?? [];
        spikePerfLog(`backbone preview fetch END: ${spikeFetchResult.length} edges`);

        // If layers already ready → render now
        if (spikeLayersReady && spikeFetchResult.length > 0) {
          const map = mapEngine.getMap();
          if (map) {
            spikePopulatePreview(map, spikeFetchResult);
            spikeStartBreath(map);
            setPreviewNetworkActive?.(true);
            spikePerfLog('previewNetworkActive → true (fetch arrived after layers)');
          }
        }
      })
      .catch((err) => { spikePerfLog(`backbone preview fetch ERROR: ${String(err)}`); });

    // ── Track B: Gate on map style ready, then create SPIKE layers ──────────
    // Same gate pattern as Fase 1A (getStyle() instead of isStyleLoaded())
    let spikeGateResolved = false;
    let spikeLoadEventHandler: (() => void) | null = null;
    const SPIKE_GATE_POLL_MS = 300;
    const SPIKE_GATE_TIMEOUT_MS = 8_000;

    const createSpikeLayers = (map: MapLibreMap): void => {
      if (spikeBackboneFiredRef.current) return;
      spikeBackboneFiredRef.current = true;

      // Fase 2C guard: style object may not be fully loaded even if getStyle() returned truthy.
      // Wrap all source/layer operations in try/catch so "Style is not done loading" is handled.
      try {
      // Create SPIKE source + layers if not already present
      if (!map.getSource(SPIKE_PREVIEW_SOURCE)) {
        map.addSource(SPIKE_PREVIEW_SOURCE, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });

        map.addLayer({
          id: SPIKE_PREVIEW_GLOW_LAYER,
          type: 'line',
          source: SPIKE_PREVIEW_SOURCE,
          filter: ['==', ['geometry-type'], 'LineString'],
          paint: {
            'line-color': '#ffffff',
            'line-width': 2,
            'line-blur': 1.5,
            'line-opacity': 0.28 * 0.5, // visible inmediatamente
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        });

        map.addLayer({
          id: SPIKE_PREVIEW_LINE_LAYER,
          type: 'line',
          source: SPIKE_PREVIEW_SOURCE,
          filter: ['==', ['geometry-type'], 'LineString'],
          paint: {
            'line-color': '#ffffff',
            'line-width': 1.5,  // igual que la red urbana normal (2px → ajustado para preview)
            'line-opacity': 0.28, // visible inmediatamente
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        });

        // Source + layers de blinkers (nodos y segmentos en el mismo source GeoJSON)
        if (!map.getSource(SPIKE_TRAVELERS_SOURCE)) {
          map.addSource(SPIKE_TRAVELERS_SOURCE, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
          });

          // Node blinkers — círculos en intersecciones reales
          map.addLayer({
            id: SPIKE_TRAVELERS_LAYER,
            type: 'circle',
            source: SPIKE_TRAVELERS_SOURCE,
            filter: ['==', ['geometry-type'], 'Point'],
            paint: {
              'circle-color': '#ffffff',
              'circle-radius': 3,
              'circle-opacity': ['coalesce', ['get', 'bOpacity'], 0],
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff',
              'circle-stroke-opacity': ['coalesce', ['get', 'bOpacity'], 0],
            },
          });

          // Segment blinkers — líneas individuales que se iluminan y apagan
          map.addLayer({
            id: SPIKE_SEGMENT_BLINKERS_LAYER,
            type: 'line',
            source: SPIKE_TRAVELERS_SOURCE,
            filter: ['==', ['geometry-type'], 'LineString'],
            paint: {
              'line-color': '#ffffff',
              'line-width': 1.5,
              'line-opacity': ['coalesce', ['get', 'bOpacity'], 0],
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          });
        }

        spikePerfLog('SPIKE source+layers created');
      } else {
        spikePerfLog('SPIKE source already existed (skip create)');
      }

      spikeLayersReady = true;
      // Spinner hidden: map is visually ready even if backbone data hasn't arrived yet
      setPreviewNetworkBooting?.(true);
      spikePerfLog('previewNetworkBooting → true (spinner hidden, map style ready)');

      // If fetch already done → render now
      if (spikeFetchDone && spikeFetchResult && spikeFetchResult.length > 0) {
        spikePopulatePreview(map, spikeFetchResult);
        spikeStartBreath(map);
        setPreviewNetworkActive?.(true);
        spikePerfLog('previewNetworkActive → true (layers ready after fetch)');
      }
      } catch (err) {
        // Style not fully loaded despite getStyle() being truthy — reset flag so gate retries
        spikeBackboneFiredRef.current = false;
        spikeLayersReady = false;
        spikePerfLog(`createSpikeLayers failed (style not ready): ${String(err)}`);
      }
    };

    const trySpikeGate = (via: string): void => {
      if (spikeGateResolved || cancelled) return;
      const map = mapEngine.getMap();
      if (!map) return;
      let styleObj: unknown;
      try { styleObj = map.getStyle(); } catch { return; }
      if (!styleObj) return;

      // Gate passed
      spikeGateResolved = true;
      spikePerfLog(`map style ready (via-${via}, isStyleLoaded=${map.isStyleLoaded()})`);
      if (spikePollInterval) { clearInterval(spikePollInterval); spikePollInterval = null; }
      if (spikeLoadEventHandler) { map.off('load', spikeLoadEventHandler); spikeLoadEventHandler = null; }
      createSpikeLayers(map);
    };

    trySpikeGate('immediate');
    if (!spikeGateResolved) {
      const mapForEvent = mapEngine.getMap();
      if (mapForEvent) {
        spikeLoadEventHandler = () => trySpikeGate('load-event');
        mapForEvent.once('load', spikeLoadEventHandler);
      }
      // Poll como fallback más lento (300ms vs 100ms anterior)
      spikePollInterval = setInterval(() => trySpikeGate('poll'), SPIKE_GATE_POLL_MS);
      window.setTimeout(() => {
        if (!spikeGateResolved) {
          spikeGateResolved = true;
          if (spikePollInterval) { clearInterval(spikePollInterval); spikePollInterval = null; }
          spikePerfLog('map gate timeout (8s) — forcing layer create attempt');
          const map = mapEngine.getMap();
          if (map) createSpikeLayers(map);
        }
      }, SPIKE_GATE_TIMEOUT_MS);
    }

    return () => {
      cancelled = true;
      if (spikePollInterval) clearInterval(spikePollInterval);
      // Quitar listener de 'load' para evitar doble ejecución tras cleanup
      if (spikeLoadEventHandler) {
        mapEngine.getMap()?.off('load', spikeLoadEventHandler);
        spikeLoadEventHandler = null;
      }
      spikeBackboneFiredRef.current = false;
      setPreviewNetworkBooting?.(false);
      // Cancel SPIKE animation frames
      if (spikeBreathRafRef.current !== null) {
        cancelAnimationFrame(spikeBreathRafRef.current);
        spikeBreathRafRef.current = null;
      }
      if (spikeFadeOutRafRef.current !== null) {
        cancelAnimationFrame(spikeFadeOutRafRef.current);
        spikeFadeOutRafRef.current = null;
      }
      setPreviewNetworkActive?.(false);
      spikeNodePositionsRef.current = [];
      spikeEdgeCoordsRef.current    = [];
      // Remove SPIKE layers + sources
      const cleanupMap = mapEngine.getMap();
      if (cleanupMap) {
        if (cleanupMap.getLayer(SPIKE_SEGMENT_BLINKERS_LAYER)) cleanupMap.removeLayer(SPIKE_SEGMENT_BLINKERS_LAYER);
        if (cleanupMap.getLayer(SPIKE_TRAVELERS_LAYER))        cleanupMap.removeLayer(SPIKE_TRAVELERS_LAYER);
        if (cleanupMap.getSource(SPIKE_TRAVELERS_SOURCE))      cleanupMap.removeSource(SPIKE_TRAVELERS_SOURCE);
        if (cleanupMap.getLayer(SPIKE_PREVIEW_NODE_LAYER))     cleanupMap.removeLayer(SPIKE_PREVIEW_NODE_LAYER);
        if (cleanupMap.getLayer(SPIKE_PREVIEW_LINE_LAYER))     cleanupMap.removeLayer(SPIKE_PREVIEW_LINE_LAYER);
        if (cleanupMap.getLayer(SPIKE_PREVIEW_GLOW_LAYER))     cleanupMap.removeLayer(SPIKE_PREVIEW_GLOW_LAYER);
        if (cleanupMap.getSource(SPIKE_PREVIEW_SOURCE))        cleanupMap.removeSource(SPIKE_PREVIEW_SOURCE);
      }
    };
  }, [projectId]); // NOT hasData — fires as soon as projectId is known
  // ── End SPIKE early backbone fetch ──────────────────────────────────────

  // ── Cargar capas cuando mapa y proyecto están listos + Registrar listener de styledata ──
  useEffect(() => {
    if (projectId && hasData) {
      spikePerfLog(`hasData=true → main network effect triggered`);
    }
    console.log('[useCanonicalNetworkLayer] 🔄 useEffect triggered:', { projectId, hasData, visible });
    const openingPerfSessionId = getActiveOpeningPerfSessionId(projectId);

    if (!projectId) {
      console.log('[useCanonicalNetworkLayer] ⚠️ No projectId, clearing layers');
      if (revealFrameRef.current !== null) {
        cancelAnimationFrame(revealFrameRef.current);
        revealFrameRef.current = null;
      }
      if (revealEndTimeoutRef.current !== null) {
        window.clearTimeout(revealEndTimeoutRef.current);
        revealEndTimeoutRef.current = null;
      }
      revealOpacityRef.current = 1;
      layersLoadedRef.current = false;
      styleListenerRegisteredRef.current = false;
      return;
    }

    // Si no hay datos, intentar cache optimista antes de remover layers
    if (!hasData) {
      let hasDataOptimistic = false;
      const cacheEntry = _readNetworkStatsCache(projectId);

      if (cacheEntry === null) {
        console.log('[NetworkCache] network:optimistic-cache-empty');
      } else if (cacheEntry.version !== NETWORK_STATS_CACHE_VERSION || cacheEntry.projectId !== projectId) {
        console.log('[NetworkCache] network:optimistic-cache-invalid', { cached: cacheEntry });
        _invalidateNetworkStatsCache(projectId, 'version-or-projectId-mismatch');
      } else {
        const ageMs = Date.now() - cacheEntry.cachedAt;
        if (ageMs > NETWORK_STATS_CACHE_TTL_MS) {
          console.log('[NetworkCache] network:optimistic-cache-expired', {
            ageMs: Math.round(ageMs),
            ttlHours: NETWORK_STATS_CACHE_TTL_MS / 3_600_000,
          });
          _invalidateNetworkStatsCache(projectId, 'ttl-expired');
        } else if (cacheEntry.edgeCount <= 0) {
          console.log('[NetworkCache] network:optimistic-cache-invalid (edgeCount=0)', { cached: cacheEntry });
          _invalidateNetworkStatsCache(projectId, 'edge-count-zero');
        } else {
          hasDataOptimistic = true;
          markOpeningPerfEvent(openingPerfSessionId, 'network:has-data-optimistic');
          console.log('[NetworkCache] network:has-data-optimistic ✨', {
            edgeCount: cacheEntry.edgeCount,
            nodeCount: cacheEntry.nodeCount,
            ageMs: Math.round(ageMs),
          });
        }
      }

      if (hasDataOptimistic) {
        // fall through — cargar red directamente sin esperar stats API
      } else {
        console.log('[useCanonicalNetworkLayer] ⚠️ No hasData, removing layers');
        if (revealFrameRef.current !== null) {
          cancelAnimationFrame(revealFrameRef.current);
          revealFrameRef.current = null;
        }
        if (revealEndTimeoutRef.current !== null) {
          window.clearTimeout(revealEndTimeoutRef.current);
          revealEndTimeoutRef.current = null;
        }
        revealOpacityRef.current = 1;
        const map = mapEngine.getMap();
        if (isMapStyleReady(map)) {
          // Remover layers en orden inverso al agregado
          if (map.getLayer(NODES_PREVIEW_LAYER_ID)) {
            map.removeLayer(NODES_PREVIEW_LAYER_ID);
          }
          if (map.getLayer(EDGES_PREVIEW_LAYER_ID)) {
            map.removeLayer(EDGES_PREVIEW_LAYER_ID);
          }
          // Edges encima, remover primero
          if (map.getLayer(EDGES_LAYER_ID)) {
            map.removeLayer(EDGES_LAYER_ID);
          }
          // Nodes debajo, remover después
          if (map.getLayer(NODES_LAYER_ID)) {
            map.removeLayer(NODES_LAYER_ID);
          }
          if (map.getSource(SOURCE_ID)) {
            map.removeSource(SOURCE_ID);
          }
        }
        layersLoadedRef.current = false;
        styleListenerRegisteredRef.current = false;
        return;
      }
    }

    if (hasData) {
      markOpeningPerfEvent(openingPerfSessionId, 'network:has-data-ready');
    }

    let pollInterval: NodeJS.Timeout | null = null;
    let timeoutId: number | null = null;
    const networkFetchAbortController = new AbortController();

    /**
     * Handler para el evento 'styledata'
     * Se dispara cuando se carga un nuevo estilo en el mapa (cambio de mapa base)
     */
    const handleStyleData = (e: any): void => {
      if (e.dataType !== 'style') return;

      const currentMap = mapEngine.getMap();
      if (!isMapStyleReady(currentMap)) return;

      const layerExists = currentMap.getLayer(EDGES_LAYER_ID);
      if (layerExists) return;

      console.log('[useCanonicalNetworkLayer] 🔄 Map style changed, reloading network layers...');

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = window.setTimeout(() => {
        layersLoadedRef.current = false;
        void loadLayers();
      }, 150);
    };

    /**
     * Carga datos de red canónica desde API y actualiza el source
     * FASE 2: Ahora usa canonicalNetworkStore como fuente de verdad
     * FASE 1A: Requests paralelos + loading state + protección contra concurrencia
     */
    const loadNetworkData = async (map: MapLibreMap): Promise<void> => {
      // FASE 1A: Protección contra cargas concurrentes
      if (loadingRef.current) {
        console.log('[useCanonicalNetworkLayer] ⚠️ Load already in progress, skipping concurrent request');
        return;
      }

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';
      const openingPerfSessionId = getActiveOpeningPerfSessionId(projectId);
      const netT0 = performance.now();
      const netMs = (): string => `+${Math.round(performance.now() - netT0)}ms`;

      try {
        // FASE 1A: Marcar como cargando (bloquea nuevas cargas)
        loadingRef.current = true;
        // FASE 1A: Iniciar loading state UI.
        // Se llama ANTES del primer await para asegurar que React lo procese en un render
        // separado del setLoadingNetwork(false) del finally. El await inmediato siguiente
        // cede el control al event loop, saliendo del commit phase del useEffect y
        // garantizando que React procese el true antes de que el fetch pueda completarse.
        setLoadingNetwork?.(true);
        // Ceder al event loop: garantiza que setLoadingNetwork(true) se procese
        // en un render distinto al setLoadingNetwork(false) final.
        await Promise.resolve();
        console.log(`[NetworkLoadPerf] ${netMs()} start — setLoadingNetwork(true)`);
        markOpeningPerfEvent(openingPerfSessionId, 'network:load-start');
        hideNetworkForReveal(map);
        console.time('[FASE 1A] Network load time');

        // Obtener token de autenticación
        const authStorage = localStorage.getItem('auth-storage');
        let token: string | null = null;
        if (authStorage) {
          const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
          token = parsed.state?.token || null;
        }

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        console.log(`[NetworkLoadPerf] ${netMs()} fetch edges+nodes start`);
        spikePerfLog('full network fetch START →');
        // FASE 1A: Requests en paralelo con AbortController para cleanup limpio
        const [edgesResponse, nodesResponse] = await Promise.all([
          fetch(`${API_URL}/projects/${projectId}/network/edges`, {
            headers,
            signal: networkFetchAbortController.signal,
          }),
          fetch(`${API_URL}/projects/${projectId}/network/nodes`, {
            headers,
            signal: networkFetchAbortController.signal,
          }),
        ]);
        console.log(`[NetworkLoadPerf] ${netMs()} fetch end — status: edges=${edgesResponse.status} nodes=${nodesResponse.status}`);

        markOpeningPerfEvent(openingPerfSessionId, 'network:fetch-end');

        // Validar ambos responses
        if (!edgesResponse.ok) {
          console.error('[useCanonicalNetworkLayer] Error al cargar edges:', edgesResponse.statusText);
          return;
        }
        if (!nodesResponse.ok) {
          console.error('[useCanonicalNetworkLayer] Error al cargar nodes:', nodesResponse.statusText);
          return;
        }

        console.log(`[NetworkLoadPerf] ${netMs()} json parse start`);
        spikePerfLog('full network HTTP responses received — parsing JSON...');
        // FASE 1A: Parsing en paralelo
        const [edges, nodes] = await Promise.all([
          edgesResponse.json(),
          nodesResponse.json()
        ]);
        console.log(`[NetworkLoadPerf] ${netMs()} json parse end — edges:${(edges as unknown[]).length} nodes:${(nodes as unknown[]).length}`);

        // ══ FASE 2: Convertir datos raw a CanonicalFeature usando utility ══
        await yieldToMainThread();
        console.log(`[NetworkLoadPerf] ${netMs()} rawArrayToFeatures start`);
        const edgeFeatures = rawArrayToFeatures(edges);
        const nodeFeatures = rawArrayToFeatures(nodes);
        const allFeatures = [...edgeFeatures, ...nodeFeatures];
        console.log(`[NetworkLoadPerf] ${netMs()} rawArrayToFeatures end — ${allFeatures.length} features total`);

        // ══ FASE 2: Inicializar store canónico (fuente de verdad) ══
        await yieldToMainThread();
        console.log(`[NetworkLoadPerf] ${netMs()} store.initialize start`);
        canonicalNetworkStore.initialize(allFeatures);
        canonicalNetworkStore.setMap(map);
        console.log(`[NetworkLoadPerf] ${netMs()} store.initialize end`);

        // ══ FASE 2: Sincronizar store → source (NO más source.setData directo) ══
        await yieldToMainThread();
        console.log(`[NetworkLoadPerf] ${netMs()} syncToSource (setData) start`);
        canonicalNetworkStore.syncToSource();
        console.log(`[NetworkLoadPerf] ${netMs()} syncToSource (setData) end`);

        markOpeningPerfEvent(openingPerfSessionId, 'network:sync-end');
        spikePerfLog(`full network synced: ${(edges as unknown[]).length} edges, ${(nodes as unknown[]).length} nodes → revealing`);
        revealNetwork(map, openingPerfSessionId);

        // Cachear stats para hasData optimista en próximas cargas
        {
          const edgeCount = (edges as unknown[]).length;
          const nodeCount = (nodes as unknown[]).length;
          if (edgeCount > 0) {
            _writeNetworkStatsCache(projectId, edgeCount, nodeCount);
          } else {
            _invalidateNetworkStatsCache(projectId, 'full-network-returned-0-edges');
          }
        }

        // SPIKE: fade out preview con leve retraso para solapar con fade-in de red real
        setTimeout(() => spikeFadeOut(map), 120);

        console.log('[useCanonicalNetworkLayer] ✓ Cargados y sincronizados con store', {
          edges: (edges as unknown[]).length,
          nodes: (nodes as unknown[]).length,
        });
        console.timeEnd('[FASE 1A] Network load time');
        console.log(`[NetworkLoadPerf] ${netMs()} complete — about to setLoadingNetwork(false)`);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('[useCanonicalNetworkLayer] Network fetch aborted (effect cleanup)');
          return;
        }
        console.error('[useCanonicalNetworkLayer] Error cargando datos:', error);
        // Invalidar cache: si el fetch falló, el cache podría estar desfasado
        _invalidateNetworkStatsCache(projectId, 'fetch-failed');
      } finally {
        // FASE 1A: Terminar loading state (garantizado con finally)
        loadingRef.current = false;
        console.log(`[NetworkLoadPerf] finally — setLoadingNetwork(false)`);
        setLoadingNetwork?.(false);
      }
    };

    const loadLayers = (): void => {
      console.log('[useCanonicalNetworkLayer] 📦 loadLayers called');
      const map = mapEngine.getMap();
      if (!map || !projectId) {
        console.log('[useCanonicalNetworkLayer] ⚠️ No map or projectId');
        return;
      }

      // ══ 1. Agregar source principal (features permanentes) ══
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          promoteId: 'id', // CRÍTICO: Promueve properties.id como feature ID para queryRenderedFeatures()
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
        console.log('[useCanonicalNetworkLayer] ✓ Source created with promoteId: "id"');

        // ══ FASE 2: Vincular store al mapa ══
        canonicalNetworkStore.setMap(map);
        console.log('[useCanonicalNetworkLayer] ✓ Store vinculado al mapa');
      }

      // ══ 1b. Agregar source de preview (features temporales) ══
      if (!map.getSource(PREVIEW_SOURCE_ID)) {
        map.addSource(PREVIEW_SOURCE_ID, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
        console.log('[useCanonicalNetworkLayer] ✓ Preview source created');
      }

      // ══ 1c. Agregar source de freehand segment preview (Etapa 1) ══
      if (!map.getSource(FREEHAND_PREVIEW_SOURCE_ID)) {
        map.addSource(FREEHAND_PREVIEW_SOURCE_ID, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
        console.log('[useCanonicalNetworkLayer] ✓ Freehand preview source created');
      }

      // ══ 2. Agregar layer de NODES (PRIMERO - debajo de edges) ══
      // UX FIX: Nodos se renderizan DEBAJO de edges para que edges tengan prioridad en hover
      if (!map.getLayer(NODES_LAYER_ID)) {
        map.addLayer({
          id: NODES_LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['==', ['geometry-type'], 'Point'], // Solo Points (ya no hay previews en este source)
          minzoom: 15, // UX: Nodos solo visibles a zoom >= 15 (escala de cuadras)
          paint: {
            // Associated > Snapped > Selected > Hover > Normal (priority order)
            'circle-radius': [
              'case',
              // Associated: radio destacado para asociación
              ['boolean', ['feature-state', 'associated'], false],
              style.nodes.radius * 1.6, // 60% más grande para asociación
              // Snap a nodo: radio MÁS grande (Regla 15)
              ['boolean', ['feature-state', 'snapped'], false],
              style.nodes.radius * 1.8, // 80% más grande para snap
              // Selected: radio más grande
              ['boolean', ['feature-state', 'selected'], false],
              style.nodes.radius * 1.5, // 50% más grande cuando seleccionado
              ['boolean', ['feature-state', 'hover'], false],
              style.nodes.radius * 1.3, // 30% más grande en hover
              style.nodes.radius,
            ],
            // Associated > Snapped > Selected > Hover > Normal (priority order)
            'circle-color': [
              'case',
              // Associated: amber para asociación con parada
              ['boolean', ['feature-state', 'associated'], false],
              '#f59e0b', // Amber - asociado a parada seleccionada
              // Snap a nodo: verde brillante (Regla 15)
              ['boolean', ['feature-state', 'snapped'], false],
              '#4CAF50', // Verde - "conectar a existente"
              // Selected: cyan WESTEER
              ['boolean', ['feature-state', 'selected'], false],
              '#2FA4B5', // Cyan WESTEER para selección
              ['boolean', ['feature-state', 'hover'], false],
              '#22D3EE', // Cyan claro para hover
              style.nodes.fillColor,
            ],
            // Associated > Snapped > Selected > Hover > Normal (priority order)
            'circle-stroke-width': [
              'case',
              // Associated: stroke ancho para destacar
              ['boolean', ['feature-state', 'associated'], false],
              3, // Stroke ancho para asociación
              // Snap a nodo: stroke muy ancho (Regla 15)
              ['boolean', ['feature-state', 'snapped'], false],
              4, // Stroke muy ancho para máximo contraste
              // Selected: stroke más ancho
              ['boolean', ['feature-state', 'selected'], false],
              style.nodes.strokeWidth * 2,
              ['boolean', ['feature-state', 'hover'], false],
              style.nodes.strokeWidth * 1.5,
              style.nodes.strokeWidth,
            ],
            // Associated > Snapped > Selected > Hover > Normal (priority order)
            'circle-stroke-color': [
              'case',
              // Associated: blanco para contraste con amber
              ['boolean', ['feature-state', 'associated'], false],
              '#FFFFFF', // Blanco para máximo contraste con amber
              // Snap a nodo: blanco para contraste (Regla 15)
              ['boolean', ['feature-state', 'snapped'], false],
              '#FFFFFF', // Blanco para máximo contraste con verde
              // Selected: blanco para contraste con cyan
              ['boolean', ['feature-state', 'selected'], false],
              '#FFFFFF',
              ['boolean', ['feature-state', 'hover'], false],
              '#22D3EE', // Cyan en hover
              style.nodes.strokeColor,
            ],
            // PERF: Hidden para ocultar nodo durante drag (evita doble render)
            'circle-opacity': createNodeOpacityExpression(style.nodes.opacity, revealOpacityRef.current),
            'circle-stroke-opacity': createNodeOpacityExpression(style.nodes.opacity, revealOpacityRef.current),
          },
          layout: {
            // UX: Nodos invisibles por defecto, solo visibles cuando isNetworkActive = true
            visibility: 'none',
          },
        });
      }

      // ══ 3. Agregar layer de EDGES (ENCIMA de nodos para prioridad en hover) ══
      if (!map.getLayer(EDGES_LAYER_ID)) {
        map.addLayer({
          id: EDGES_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          filter: ['==', ['geometry-type'], 'LineString'], // Solo LineStrings (ya no hay previews en este source)
          paint: {
            // ═══════════════════════════════════════════════════════════════════
            // PRIORIDAD VISUAL DE FEATURE STATES (orden descendente):
            //
            // 1. associated (#f59e0b amber)       - Asociación con punto externo
            // 2. snapped (#2196F3 azul)           - Snap activo para dividir
            // 3. multiMergeValid (#10b981 verde)  - Multi-Merge V1: cadena válida ✓
            // 4. multiMergeSelected (#3b82f6 azul)- Multi-Merge V1: edge en selección
            // 5. mergeValid (#10b981 verde)       - V1 2-edge: 2 edges válidos ✓
            // 6. mergeSelected (#06b6d4 cyan)     - V1 2-edge: edge en selección
            // 7. selected (#2FA4B5 cyan)          - Selección general
            // 8. hover (#60D0E8 cyan claro)       - Hover del cursor
            // 9. normal (configurable)            - Estado base
            //
            // IMPORTANTE: Multi-Merge V1 tiene prioridad sobre V1 2-edge merge.
            // Ambos sistemas pueden coexistir sin conflictos.
            // ═══════════════════════════════════════════════════════════════════
            'line-color': [
              'case',
              // 1. Associated: amber para mostrar asociación con parada
              ['boolean', ['feature-state', 'associated'], false],
              '#f59e0b', // Amber - asociado a parada seleccionada
              // 2. Snap a edge: azul brillante (Regla 15)
              ['boolean', ['feature-state', 'snapped'], false],
              '#2196F3', // Azul - "dividir segmento"
              // 3. Multi-Merge V1: Cadena válida - verde
              ['boolean', ['feature-state', 'multiMergeValid'], false],
              '#10b981', // Verde - cadena válida para multi-merge
              // 4. Multi-Merge V1: Edge seleccionado - azul
              ['boolean', ['feature-state', 'multiMergeSelected'], false],
              '#3b82f6', // Azul - edge seleccionado para multi-merge
              // 5. V1 2-edge: Merge Valid - verde
              ['boolean', ['feature-state', 'mergeValid'], false],
              '#10b981', // Verde - 2 edges válidos para merge
              // 6. V1 2-edge: Merge Selected - cyan
              ['boolean', ['feature-state', 'mergeSelected'], false],
              '#06b6d4', // Cyan brillante - edge seleccionado para merge
              // 7. Selected: cyan oscuro para selección general
              ['boolean', ['feature-state', 'selected'], false],
              '#2FA4B5', // Cyan WESTEER para selección (oscuro)
              // 8. Hover: cyan medio
              ['boolean', ['feature-state', 'hover'], false],
              '#60D0E8', // Cyan medio para hover (más visible)
              // 9. Normal: color base configurable
              style.edges.color, // Color normal
            ],
            // Associated > Snapped > Multi-Merge > Merge States > Selected > Hover > Normal (priority order)
            'line-width': [
              'case',
              // Associated: ancho destacado
              ['boolean', ['feature-state', 'associated'], false],
              style.edges.width * 2.0, // 100% más ancho para asociación
              // Snap a edge: MUY ancho para destacar (Regla 15)
              ['boolean', ['feature-state', 'snapped'], false],
              style.edges.width * 2.5, // 150% más ancho para snap
              // Multi-Merge V1: ancho destacado (prioridad sobre V1 2-edge)
              ['boolean', ['feature-state', 'multiMergeSelected'], false],
              style.edges.width * 2.2, // 120% más ancho para edge en multi-merge
              ['boolean', ['feature-state', 'multiMergeValid'], false],
              style.edges.width * 2.2, // 120% más ancho para cadena válida
              // V1 2-edge: Merge Selected/Valid/Invalid: ancho destacado
              ['boolean', ['feature-state', 'mergeSelected'], false],
              style.edges.width * 2.2, // 120% más ancho para edge en selección de merge
              ['boolean', ['feature-state', 'selected'], false],
              style.edges.width * 2.2, // 120% más ancho cuando está seleccionado
              ['boolean', ['feature-state', 'hover'], false],
              style.edges.width * 1.8, // 80% más ancho en hover (más visible)
              style.edges.width, // Ancho normal
            ],
            'line-opacity': createEdgeOpacityExpression(style.edges.opacity, revealOpacityRef.current),
          },
          layout: {
            'line-cap': style.edges.cap,
            'line-join': style.edges.join,
            visibility: visibleRef.current ? 'visible' : 'none',
          },
        });
      }

      // ══ 4. Agregar layer de EDGES PREVIEW (líneas punteadas cyan) ══
      if (!map.getLayer(EDGES_PREVIEW_LAYER_ID)) {
        map.addLayer({
          id: EDGES_PREVIEW_LAYER_ID,
          type: 'line',
          source: PREVIEW_SOURCE_ID, // Source separado para previews
          filter: ['==', ['geometry-type'], 'LineString'],
          paint: {
            'line-color': '#22D3EE', // Cyan
            'line-width': 3,
            'line-opacity': 0.8,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
            // UX: Preview invisible por defecto, solo visible en modo edición
            visibility: 'none',
          },
        });

        // Agregar dasharray para estilo punteado (estático, sin animación)
        map.setPaintProperty(EDGES_PREVIEW_LAYER_ID, 'line-dasharray', [2, 2]);

        // NOTA: Animación de dasharray desactivada temporalmente
        // Causaba error "LineAtlas out of space" en MapLibre
        // TODO: Implementar animación con line-offset o line-gradient en el futuro
      }

      // ══ 5. Agregar layer de NODES PREVIEW (círculos cyan) ══
      if (!map.getLayer(NODES_PREVIEW_LAYER_ID)) {
        map.addLayer({
          id: NODES_PREVIEW_LAYER_ID,
          type: 'circle',
          source: PREVIEW_SOURCE_ID, // Source separado para previews
          filter: ['==', ['geometry-type'], 'Point'],
          minzoom: 15, // UX: Preview de nodos solo visible a zoom >= 15 (consistente con layer principal)
          paint: {
            'circle-radius': 8,
            'circle-color': '#22D3EE', // Cyan
            'circle-opacity': 0.6,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-opacity': 1,
          },
          layout: {
            // UX: Preview invisible por defecto, solo visible en modo edición
            visibility: 'none',
          },
        });
      }

      // ══ 5b. Agregar layer de FREEHAND SEGMENT PREVIEW - LINE (Etapa 1) ══
      if (!map.getLayer(FREEHAND_PREVIEW_LINE_LAYER_ID)) {
        map.addLayer({
          id: FREEHAND_PREVIEW_LINE_LAYER_ID,
          type: 'line',
          source: FREEHAND_PREVIEW_SOURCE_ID,
          filter: ['==', ['geometry-type'], 'LineString'],
          paint: {
            'line-color': '#f97316', // Naranja
            'line-width': 3,
            'line-dasharray': [4, 2], // Dashed
            'line-opacity': 0.9,
          },
          layout: {
            visibility: 'none', // Invisible por defecto
          },
        });
        console.log('[useCanonicalNetworkLayer] ✓ Freehand preview line layer created');
      }

      // ══ 5c. Agregar layer de FREEHAND SEGMENT PREVIEW - POINTS (Etapa 1) ══
      if (!map.getLayer(FREEHAND_PREVIEW_POINTS_LAYER_ID)) {
        map.addLayer({
          id: FREEHAND_PREVIEW_POINTS_LAYER_ID,
          type: 'circle',
          source: FREEHAND_PREVIEW_SOURCE_ID,
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': 5,
            'circle-color': '#f97316', // Naranja
            'circle-opacity': 0.9,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-opacity': 1,
          },
          layout: {
            visibility: 'none', // Invisible por defecto
          },
        });
        console.log('[useCanonicalNetworkLayer] ✓ Freehand preview points layer created');
      }

      // ══ 6. Cargar datos desde API ══
      void loadNetworkData(map);

      layersLoadedRef.current = true;
      console.log('[useCanonicalNetworkLayer] ✅ Canonical network layers loaded');
    };

    // ── FASE 1A: Gate robusto para creación de layers ──────────────────────
    // Reemplaza el polling rígido sobre map.isStyleLoaded() que causaba ~125s de bloqueo.
    // Estrategia: getStyle() check → load event → polling prudente → timeout de seguridad.
    let gateResolved = false;
    let loadEventHandler: (() => void) | null = null;
    const GATE_POLL_INTERVAL_MS = 500;
    const GATE_TIMEOUT_MS = 10_000;

    /**
     * Intenta crear layers en el mapa.
     * No depende de isStyleLoaded() como gate bloqueante — usa getStyle() como
     * indicador mínimo de que el estilo base está parseado y el mapa acepta addSource.
     */
    const tryLoadLayers = (via: string): void => {
      if (gateResolved) return;

      const map = mapEngine.getMap();
      if (!map) {
        console.log('[NetworkGate] waiting-map');
        return;
      }

      // Verificar que el objeto de estilo exista (estilo base parseado).
      // getStyle() retorna undefined/null si el estilo aún no se cargó.
      let styleObj: unknown;
      try {
        styleObj = map.getStyle();
      } catch {
        console.log('[NetworkGate] waiting-style-object');
        return;
      }
      if (!styleObj) {
        console.log('[NetworkGate] waiting-style-object');
        return;
      }

      // Gate superado — intentar crear layers
      gateResolved = true;
      markOpeningPerfEvent(openingPerfSessionId, 'network:map-ready-gate-passed');
      console.log(`[NetworkGate] ready-via-${via} (isStyleLoaded=${map.isStyleLoaded()})`);

      // Limpiar polling y listeners ya que el gate se resolvió
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
      if (loadEventHandler) {
        map.off('load', loadEventHandler);
        loadEventHandler = null;
      }

      // Si layers ya existen (re-render, StrictMode), no recrear
      if (layersLoadedRef.current && map.getLayer(EDGES_LAYER_ID)) {
        return;
      }

      // Crear layers con protección contra errores de MapLibre
      try {
        loadLayers();
        console.log('[NetworkGate] layer-creation-success');
      } catch (err) {
        console.error('[NetworkGate] layer-creation-failed', err);
        // Reintentar una vez después de un breve delay
        gateResolved = false;
        window.setTimeout(() => {
          if (!gateResolved) {
            console.log('[NetworkGate] retry-after-failure');
            tryLoadLayers('retry');
          }
        }, 1000);
        return;
      }

      // Registrar listener de styledata UNA VEZ que el mapa está listo
      if (!styleListenerRegisteredRef.current) {
        console.log('[useCanonicalNetworkLayer] 🎧 Registering styledata listener');
        map.on('styledata', handleStyleData);
        styleListenerRegisteredRef.current = true;
      }
    };

    // 1. Intentar crear layers inmediatamente
    tryLoadLayers('immediate');

    if (!gateResolved) {
      const map = mapEngine.getMap();

      // 2. Escuchar evento 'load' como vía principal (más confiable que polling)
      if (map) {
        loadEventHandler = () => tryLoadLayers('load-event');
        map.once('load', loadEventHandler);
      }

      // 3. Polling como fallback con intervalo prudente (500ms, no 100ms)
      console.log('[NetworkGate] waiting — load-event registered, poll fallback started');
      pollInterval = setInterval(() => tryLoadLayers('poll'), GATE_POLL_INTERVAL_MS);

      // 4. Timeout de seguridad: después de 10s, forzar intento si el mapa existe
      timeoutId = window.setTimeout(() => {
        if (gateResolved) return;
        const forceMap = mapEngine.getMap();
        if (forceMap) {
          console.warn(`[NetworkGate] timeout-force-attempt (${GATE_TIMEOUT_MS}ms elapsed)`);
          tryLoadLayers('timeout');
          // Si aún no se resolvió después del timeout, intentar con getStyle() más permisivo
          if (!gateResolved) {
            console.warn('[NetworkGate] timeout-force-attempt failed — forcing gate');
            gateResolved = true;
            markOpeningPerfEvent(openingPerfSessionId, 'network:map-ready-gate-passed');
            if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
            if (loadEventHandler) { forceMap.off('load', loadEventHandler); loadEventHandler = null; }
            try {
              loadLayers();
              console.log('[NetworkGate] layer-creation-success (forced)');
            } catch (err) {
              console.error('[NetworkGate] layer-creation-failed (forced)', err);
            }
          }
        }
      }, GATE_TIMEOUT_MS) as unknown as number;
    }

    // Cleanup
    return () => {
      // Abortar fetch en vuelo (evita requests huérfanas en StrictMode y cambios de proyecto)
      networkFetchAbortController.abort();
      // Liberar el guard de carga concurrente para que el re-run del effect
      // (React StrictMode / re-renders) pueda disparar un nuevo loadNetworkData.
      loadingRef.current = false;
      // FASE 1A: Marcar gate como resuelto para evitar callbacks huérfanos
      gateResolved = true;

      if (pollInterval) {
        clearInterval(pollInterval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // FASE 1A: Limpiar listener de load event si fue registrado
      if (loadEventHandler) {
        const cleanupMap = mapEngine.getMap();
        if (cleanupMap) cleanupMap.off('load', loadEventHandler);
        loadEventHandler = null;
      }
      if (revealEndTimeoutRef.current !== null) {
        window.clearTimeout(revealEndTimeoutRef.current);
        revealEndTimeoutRef.current = null;
      }
      if (revealFrameRef.current !== null) {
        cancelAnimationFrame(revealFrameRef.current);
        revealFrameRef.current = null;
      }
      const currentMap = mapEngine.getMap();
      if (currentMap) {
        if (styleListenerRegisteredRef.current) {
          currentMap.off('styledata', handleStyleData);
          styleListenerRegisteredRef.current = false;
        }
        if (layersLoadedRef.current && isMapStyleReady(currentMap)) {
          // FASE 5.1: Limpiar feature-states antes de remover layers
          const source = currentMap.getSource(SOURCE_ID);
          if (source) {
            const allFeatures = currentMap.querySourceFeatures(SOURCE_ID);
            allFeatures.forEach((feature) => {
              if (feature.id !== undefined) {
                currentMap.removeFeatureState(
                  { source: SOURCE_ID, id: feature.id },
                  'snapped'
                );
                currentMap.removeFeatureState(
                  { source: SOURCE_ID, id: feature.id },
                  'hover'
                );
                currentMap.removeFeatureState(
                  { source: SOURCE_ID, id: feature.id },
                  'hidden'
                );
                currentMap.removeFeatureState(
                  { source: SOURCE_ID, id: feature.id },
                  'associated'
                );
              }
            });
          }

          // Remover layers en orden inverso al agregado (último agregado, primero removido)
          if (currentMap.getLayer(NODES_PREVIEW_LAYER_ID)) {
            currentMap.removeLayer(NODES_PREVIEW_LAYER_ID);
          }
          if (currentMap.getLayer(EDGES_PREVIEW_LAYER_ID)) {
            currentMap.removeLayer(EDGES_PREVIEW_LAYER_ID);
          }
          // Edges encima, remover primero
          if (currentMap.getLayer(EDGES_LAYER_ID)) {
            currentMap.removeLayer(EDGES_LAYER_ID);
          }
          // Nodes debajo, remover después
          if (currentMap.getLayer(NODES_LAYER_ID)) {
            currentMap.removeLayer(NODES_LAYER_ID);
          }
          if (currentMap.getSource(SOURCE_ID)) {
            currentMap.removeSource(SOURCE_ID);
          }
          layersLoadedRef.current = false;
        }
      }
    };
  }, [projectId, hasData]);

  // ── Actualizar visibilidad ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoadedRef.current || !isMapStyleReady(map)) return;

    // Edges: visibles cuando visible = true
    const edgesVisibility = visible ? 'visible' : 'none';

    // Nodes: visibles SOLO cuando visible = true Y isNetworkInteractive = true (UX: solo en modo interactivo)
    const nodesVisibility = (visible && isNetworkInteractive) ? 'visible' : 'none';

    // Layers principales
    if (map.getLayer(EDGES_LAYER_ID)) {
      map.setLayoutProperty(EDGES_LAYER_ID, 'visibility', edgesVisibility);
    }
    if (map.getLayer(NODES_LAYER_ID)) {
      map.setLayoutProperty(NODES_LAYER_ID, 'visibility', nodesVisibility);
    }

    // Layers de preview (solo visibles en modo interactivo)
    const previewVisibility = (visible && isNetworkInteractive) ? 'visible' : 'none';
    if (map.getLayer(EDGES_PREVIEW_LAYER_ID)) {
      map.setLayoutProperty(EDGES_PREVIEW_LAYER_ID, 'visibility', previewVisibility);
    }
    if (map.getLayer(NODES_PREVIEW_LAYER_ID)) {
      map.setLayoutProperty(NODES_PREVIEW_LAYER_ID, 'visibility', previewVisibility);
    }
  }, [visible, isNetworkInteractive]);

  // ── Actualizar estilos dinámicamente cuando cambian ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoadedRef.current || !isMapStyleReady(map)) return;

    // Actualizar estilos de EDGES
    // IMPORTANTE: Reconstruir expresiones completas para preservar feature-state
    if (map.getLayer(EDGES_LAYER_ID)) {
      // Line color con feature-state (DEBE coincidir con prioridad de layer creation)
      // Prioridad: associated > snapped > mergeValid > mergeSelected > selected > hover > normal
      map.setPaintProperty(EDGES_LAYER_ID, 'line-color', [
        'case',
        ['boolean', ['feature-state', 'associated'], false],
        '#f59e0b', // 1. Amber - asociado a parada seleccionada
        ['boolean', ['feature-state', 'snapped'], false],
        '#2196F3', // 2. Azul - "dividir segmento"
        ['boolean', ['feature-state', 'mergeValid'], false],
        '#10b981', // 3. Verde - 2 edges válidos para merge (Fase 3)
        ['boolean', ['feature-state', 'mergeSelected'], false],
        '#06b6d4', // 4. Cyan brillante - edge seleccionado para merge (Fase 3)
        ['boolean', ['feature-state', 'selected'], false],
        '#2FA4B5', // 5. Cyan WESTEER para selección (oscuro)
        ['boolean', ['feature-state', 'hover'], false],
        '#60D0E8', // 6. Cyan medio para hover (más visible)
        style.edges.color, // 7. Color normal desde el estilo
      ]);

      // Line width con feature-state para associated, merge states, selected y hover
      map.setPaintProperty(EDGES_LAYER_ID, 'line-width', [
        'case',
        ['boolean', ['feature-state', 'associated'], false],
        style.edges.width * 2.0, // 100% más ancho para asociación
        ['boolean', ['feature-state', 'snapped'], false],
        style.edges.width * 2.5, // 150% más ancho para snap
        ['boolean', ['feature-state', 'mergeSelected'], false],
        style.edges.width * 2.2, // 120% más ancho para edge en selección de merge (Fase 3)
        ['boolean', ['feature-state', 'selected'], false],
        style.edges.width * 2.2, // 120% más ancho cuando está seleccionado
        ['boolean', ['feature-state', 'hover'], false],
        style.edges.width * 1.8, // 80% más ancho en hover (más visible)
        style.edges.width, // Ancho normal desde el estilo
      ]);

      // Line opacity con feature-state para hidden, associated, selected y hover
      map.setPaintProperty(
        EDGES_LAYER_ID,
        'line-opacity',
        createEdgeOpacityExpression(style.edges.opacity, revealOpacityRef.current)
      );

      map.setLayoutProperty(EDGES_LAYER_ID, 'line-cap', style.edges.cap);
      map.setLayoutProperty(EDGES_LAYER_ID, 'line-join', style.edges.join);
    }

    // Actualizar estilos de NODES
    // IMPORTANTE: Reconstruir expresiones completas para preservar feature-state
    // FIX: Agregado 'selected' y 'associated' feature-states
    if (map.getLayer(NODES_LAYER_ID)) {
      // Circle radius con feature-state para associated, selected y hover
      map.setPaintProperty(NODES_LAYER_ID, 'circle-radius', [
        'case',
        ['boolean', ['feature-state', 'associated'], false],
        style.nodes.radius * 1.6, // 60% más grande para asociación
        ['boolean', ['feature-state', 'selected'], false],
        style.nodes.radius * 1.5, // 50% más grande cuando seleccionado
        ['boolean', ['feature-state', 'hover'], false],
        style.nodes.radius * 1.3, // 30% más grande en hover
        style.nodes.radius,
      ]);

      // Circle color con feature-state para associated, selected y hover
      map.setPaintProperty(NODES_LAYER_ID, 'circle-color', [
        'case',
        ['boolean', ['feature-state', 'associated'], false],
        '#f59e0b', // Amber para asociación
        ['boolean', ['feature-state', 'selected'], false],
        '#2FA4B5', // Cyan WESTEER para selección
        ['boolean', ['feature-state', 'hover'], false],
        '#22D3EE', // Cyan claro para hover
        style.nodes.fillColor,
      ]);

      // Circle stroke width con feature-state para associated, selected y hover
      map.setPaintProperty(NODES_LAYER_ID, 'circle-stroke-width', [
        'case',
        ['boolean', ['feature-state', 'associated'], false],
        3, // Stroke ancho para asociación
        ['boolean', ['feature-state', 'selected'], false],
        style.nodes.strokeWidth * 2,
        ['boolean', ['feature-state', 'hover'], false],
        style.nodes.strokeWidth * 1.5,
        style.nodes.strokeWidth,
      ]);

      // Circle stroke color con feature-state para associated, selected y hover
      map.setPaintProperty(NODES_LAYER_ID, 'circle-stroke-color', [
        'case',
        ['boolean', ['feature-state', 'associated'], false],
        '#FFFFFF', // Blanco para contraste con amber
        ['boolean', ['feature-state', 'selected'], false],
        '#FFFFFF', // Blanco para contraste con cyan
        ['boolean', ['feature-state', 'hover'], false],
        '#22D3EE', // Cyan en hover
        style.nodes.strokeColor,
      ]);

      // PERF: Preservar hidden feature-state en actualizaciones de estilo
      const nodeOpacityExpression = createNodeOpacityExpression(
        style.nodes.opacity,
        revealOpacityRef.current
      );
      map.setPaintProperty(NODES_LAYER_ID, 'circle-opacity', nodeOpacityExpression);
      map.setPaintProperty(NODES_LAYER_ID, 'circle-stroke-opacity', nodeOpacityExpression);
    }
  }, [style]);

  // ── Aplicar highlight visual al edge seleccionado ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoadedRef.current || !isMapStyleReady(map)) return;

    // Remover selección anterior si cambió
    if (selectedEdgeIdRef.current && selectedEdgeIdRef.current !== selectedEdgeId) {
      map.removeFeatureState(
        { source: SOURCE_ID, id: selectedEdgeIdRef.current },
        'selected'
      );
      selectedEdgeIdRef.current = null;
    }

    // Aplicar nueva selección
    if (selectedEdgeId) {
      map.setFeatureState(
        { source: SOURCE_ID, id: selectedEdgeId },
        { selected: true }
      );
      selectedEdgeIdRef.current = selectedEdgeId;
    }
  }, [selectedEdgeId]);

  // ── Aplicar highlight visual a edges seleccionados para merge (Fase 3) ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoadedRef.current || !isMapStyleReady(map)) return;

    // Determinar estado visual basado en validación
    // Solo hay 2 estados visuales: mergeSelected (1 edge) y mergeValid (2 edges válidos)
    const isValid = mergeValidationResult?.valid === true;

    // Remover feature states de edges que ya no están seleccionados
    const prevSelected = selectedEdgesForMergeRef.current;
    prevSelected.forEach((edgeId) => {
      if (!selectedEdgesForMerge.includes(edgeId)) {
        map.removeFeatureState(
          { source: SOURCE_ID, id: edgeId },
          'mergeSelected'
        );
        map.removeFeatureState(
          { source: SOURCE_ID, id: edgeId },
          'mergeValid'
        );
      }
    });

    // Aplicar feature states a edges actualmente seleccionados
    selectedEdgesForMerge.forEach((edgeId) => {
      map.setFeatureState(
        { source: SOURCE_ID, id: edgeId },
        {
          mergeSelected: true,
          mergeValid: isValid,
        }
      );
    });

    // Actualizar ref
    selectedEdgesForMergeRef.current = [...selectedEdgesForMerge];
  }, [selectedEdgesForMerge, mergeValidationResult]);

  // ── Aplicar highlight visual a edges en Multi-Merge V1 selection ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoadedRef.current || !isMapStyleReady(map)) return;

    // Determinar si la cadena es válida
    const isValid = chainValidation?.isValid === true;

    // Remover feature states de edges que ya no están seleccionados
    const prevSelected = selectedEdgeIdsRef.current;
    prevSelected.forEach((edgeId) => {
      if (!selectedEdgeIds.includes(edgeId)) {
        map.removeFeatureState(
          { source: SOURCE_ID, id: edgeId },
          'multiMergeSelected'
        );
        map.removeFeatureState(
          { source: SOURCE_ID, id: edgeId },
          'multiMergeValid'
        );
      }
    });

    // Aplicar feature states a edges actualmente seleccionados
    selectedEdgeIds.forEach((edgeId) => {
      map.setFeatureState(
        { source: SOURCE_ID, id: edgeId },
        {
          multiMergeSelected: true,
          multiMergeValid: isValid,
        }
      );
    });

    // Actualizar ref
    selectedEdgeIdsRef.current = [...selectedEdgeIds];

    console.log('[useCanonicalNetworkLayer] Multi-Merge V1 selection updated:', {
      count: selectedEdgeIds.length,
      isValid,
    });
  }, [selectedEdgeIds, chainValidation]);

  // ── Actualizar preview de freehand segment (Etapa 1) ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoadedRef.current || !isMapStyleReady(map)) return;

    const source = map.getSource(FREEHAND_PREVIEW_SOURCE_ID) as any;
    if (!source) return;

    // Si no hay preview edge, limpiar source y ocultar layers
    if (!freehandPreviewEdge) {
      source.setData({ type: 'FeatureCollection', features: [] });
      if (map.getLayer(FREEHAND_PREVIEW_LINE_LAYER_ID)) {
        map.setLayoutProperty(FREEHAND_PREVIEW_LINE_LAYER_ID, 'visibility', 'none');
      }
      if (map.getLayer(FREEHAND_PREVIEW_POINTS_LAYER_ID)) {
        map.setLayoutProperty(FREEHAND_PREVIEW_POINTS_LAYER_ID, 'visibility', 'none');
      }
      return;
    }

    // Construir features: line + intermediate points
    const features: any[] = [];

    // Feature de línea (preview acumulativo)
    features.push({
      type: 'Feature',
      geometry: freehandPreviewEdge.geometry,
      properties: { type: 'preview-line' },
    });

    // Features de shape points intermedios
    freehandIntermediatePoints.forEach((coords, index) => {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: coords },
        properties: { type: 'intermediate-point', index },
      });
    });

    // Actualizar source
    source.setData({ type: 'FeatureCollection', features });

    // Mostrar layers
    if (map.getLayer(FREEHAND_PREVIEW_LINE_LAYER_ID)) {
      map.setLayoutProperty(FREEHAND_PREVIEW_LINE_LAYER_ID, 'visibility', 'visible');
    }
    if (map.getLayer(FREEHAND_PREVIEW_POINTS_LAYER_ID)) {
      map.setLayoutProperty(FREEHAND_PREVIEW_POINTS_LAYER_ID, 'visibility', 'visible');
    }

    console.log('[useCanonicalNetworkLayer] Freehand preview updated:', {
      intermediatePoints: freehandIntermediatePoints.length,
      totalFeatures: features.length,
    });
  }, [freehandPreviewEdge, freehandIntermediatePoints]);

  // ── Aplicar highlight visual al nodo seleccionado ──
  // TEMPORALMENTE DESHABILITADO para diagnóstico de congelamiento
  // TODO: Re-habilitar después de encontrar la causa del bug
  /*
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoadedRef.current || !isMapStyleReady(map)) return;

    // Remover selección anterior
    if (selectedNodeIdRef.current && selectedNodeIdRef.current !== selectedNodeId) {
      map.removeFeatureState(
        { source: SOURCE_ID, id: selectedNodeIdRef.current },
        'selected'
      );
    }

    // Aplicar nueva selección
    if (selectedNodeId) {
      map.setFeatureState(
        { source: SOURCE_ID, id: selectedNodeId },
        { selected: true }
      );
      selectedNodeIdRef.current = selectedNodeId;
      console.log('[useCanonicalNetworkLayer] ✓ Node selected:', selectedNodeId);
    } else {
      selectedNodeIdRef.current = null;
    }
  }, [selectedNodeId]);
  */

  // ── Aplicar hover visual al edge con hover ──
  // DESACTIVADO: El hover ahora se maneja directamente en useNetworkEditInteractivity
  // sin pasar por React state, para evitar race conditions y re-renders innecesarios.
  // Los event handlers de MapLibre (mousemove/mouseleave) aplican feature-states directamente.

  // ── Aplicar hover visual al nodo con hover ──
  // REFACTOR: Único lugar que aplica feature-state de hover para nodos
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoadedRef.current || !isMapStyleReady(map)) return;

    // NO aplicar hover durante modo de creación de rama
    if (editModeRef.current === 'create-branch') {
      if (hoveredNodeIdRef.current) {
        map.removeFeatureState(
          { source: SOURCE_ID, id: hoveredNodeIdRef.current },
          'hover'
        );
        hoveredNodeIdRef.current = null;
      }
      return;
    }

    // Remover hover anterior
    if (hoveredNodeIdRef.current && hoveredNodeIdRef.current !== hoveredNodeId) {
      map.removeFeatureState(
        { source: SOURCE_ID, id: hoveredNodeIdRef.current },
        'hover'
      );
    }

    // Aplicar nuevo hover
    if (hoveredNodeId) {
      map.setFeatureState(
        { source: SOURCE_ID, id: hoveredNodeId },
        { hover: true }
      );
      hoveredNodeIdRef.current = hoveredNodeId;
    } else {
      hoveredNodeIdRef.current = null;
    }
  }, [hoveredNodeId, editMode]);

  // REFACTOR: Se eliminó el handler duplicado de hover (handleMouseMove/handleMouseLeave)
  // La detección de hover ahora se hace ÚNICAMENTE en useNetworkEditInteractivity
  // Este hook solo aplica los feature-states basado en hoveredEdgeId/hoveredNodeId del contexto

  // ── Escuchar evento de nodo actualizado (drag & drop) ──
  useEffect(() => {
    const handleNodeUpdated = (): void => {
      console.log('[useCanonicalNetworkLayer] 🔄 Node updated event received, reloading network data...');
      const map = mapEngine.getMap();
      if (map && projectId) {
        // Recargar datos de la red
        const loadNetworkData = async (map: MapLibreMap): Promise<void> => {
          const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

          try {
            const authStorage = localStorage.getItem('auth-storage');
            let token: string | null = null;
            if (authStorage) {
              const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
              token = parsed.state?.token || null;
            }

            const headers: Record<string, string> = {
              'Content-Type': 'application/json',
            };
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }

            const edgesResponse = await fetch(
              `${API_URL}/projects/${projectId}/network/edges`,
              { headers }
            );
            const edges = await edgesResponse.json();

            const nodesResponse = await fetch(
              `${API_URL}/projects/${projectId}/network/nodes`,
              { headers }
            );
            const nodes = await nodesResponse.json();

            const features = [
              ...edges.map((edge: any) => ({
                type: 'Feature',
                geometry: edge.geometry,
                properties: {
                  id: edge.id, // ID en properties (usado por promoteId)
                  type: 'edge',
                  roadClass: edge.roadClass,
                  // FIX: El backend devuelve fromNode/toNode como objetos, no como IDs directos
                  fromNodeId: edge.fromNode?.id || edge.fromNodeId,
                  toNodeId: edge.toNode?.id || edge.toNodeId,
                  lengthMeters: edge.lengthMeters,
                },
              })),
              ...nodes.map((node: any) => ({
                type: 'Feature',
                geometry: node.geometry,
                properties: {
                  id: node.id, // ID en properties (usado por promoteId)
                  type: 'node',
                  intersectionType: node.intersectionType,
                },
              })),
            ];

            const geojson = {
              type: 'FeatureCollection',
              features,
            };

            // 🔍 DEBUG BUG 2: Validar estado del store ANTES de actualizar source
            const storeStatsBefore = canonicalNetworkStore.getStats();
            console.log('[DEBUG BUG2] BEFORE source.setData():', {
              featuresInStore_nodes: storeStatsBefore.nodes,
              featuresInStore_edges: storeStatsBefore.edges,
              featuresInStore_total: storeStatsBefore.nodes + storeStatsBefore.edges,
              featuresInSourcePayload: features.length,
              expectedBehavior: 'Store and source should be in sync',
            });

            const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource;
            if (source) {
              source.setData(geojson as any);
              console.log('[useCanonicalNetworkLayer] ✓ Network data reloaded after node update');

              // 🔍 DEBUG BUG 2: Validar estado del store DESPUÉS de actualizar source
              const storeStatsAfter = canonicalNetworkStore.getStats();
              console.log('[DEBUG BUG2] AFTER source.setData():', {
                featuresInStore_nodes: storeStatsAfter.nodes,
                featuresInStore_edges: storeStatsAfter.edges,
                featuresInStore_total: storeStatsAfter.nodes + storeStatsAfter.edges,
                featuresInSource: features.length,
                desync: (storeStatsAfter.nodes + storeStatsAfter.edges) !== features.length,
                expectedBehavior: 'Store should have same count as source, but it does NOT because we bypassed store',
              });
            }
          } catch (error) {
            console.error('[useCanonicalNetworkLayer] Error reloading network data:', error);
          }
        };

        void loadNetworkData(map);
      }
    };

    window.addEventListener('network-node-updated', handleNodeUpdated);
    return () => window.removeEventListener('network-node-updated', handleNodeUpdated);
  }, [projectId]);
}
